/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Load the self-hosted TradingView Charting Library from the SAME folder as
 * its bundles, at runtime.
 *
 * The loader and the bundles must be the same build: each loader asks for
 * files named with its own internal id and speaks its own iframe protocol. The
 * vendored src/charting_library loader is v23.038 (id e012cb8f) while the
 * deployed bundle is v22.032 (id e2a841ff); mounting that pair threw
 * "widgetReady is not a function" as an uncaught error over the whole page.
 * Loading `charting_library.standalone.js` from the deployed folder makes a
 * mismatch impossible — whatever bundle is deployed brings its own loader.
 */
export const LIBRARY_PATH = "/charting_library/";
const SCRIPT_SRC = `${LIBRARY_PATH}charting_library.standalone.js`;

let pending: Promise<any> | null = null;

/** Resolves with the library's `widget` constructor, or rejects if absent. */
export const loadChartingLibrary = (): Promise<any> => {
  if (typeof window === "undefined") return Promise.reject(new Error("no DOM"));
  if (pending) return pending;

  pending = (async () => {
    // The dev server answers a missing file with index.html (200), so a bare
    // <script> would "load" and then fail to parse. Check what is there first.
    const head = await fetch(SCRIPT_SRC, { method: "HEAD" });
    if (!head.ok || !/javascript/i.test(head.headers.get("content-type") ?? "")) {
      throw new Error("TradingView Charting Library is not deployed");
    }
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("TradingView Charting Library failed to load"));
      document.head.appendChild(script);
    });
    const lib = (window as any).TradingView;
    if (typeof lib?.widget !== "function") {
      throw new Error("TradingView Charting Library loaded without a widget constructor");
    }
    return lib.widget;
  })().catch((e) => {
    pending = null; // allow a retry on the next mount
    throw e;
  });

  return pending;
};
